import { detectCountry } from './countryDetection';

/**
 * Finland legal-warning badge decision (pure, unit-testable).
 *
 * Finnish law (maastoliikennelaki / jokamiehenoikeus) does NOT cover motor vehicles:
 * overnighting a vehicle is legal ONLY on public roads, parking, rest areas
 * (levähdysalue) or campsites — not in free nature. The app still DISCOVERS Finnish
 * nature/pull-off spots (same as Sweden) but flags the non-official ones with an amber
 * "sjekk lokalt" warning badge. Official rest areas / parking get NO badge.
 *
 * Primary signal is the persisted server flag `fiLegalWarning` (set by scanAreaBatch).
 * The country + source fallback covers spots that predate the flag or arrive via other
 * paths, mirroring the server's `isFinlandLegalVehicleSurface` decision.
 */

export interface FinlandLegalWarningSpot {
  location?: { latitude?: number; longitude?: number };
  fiLegalWarning?: boolean;
  source?: string;
  spotType?: string;
  roadOwnerCode?: number | null;
  restType?: string | null;
  features?: unknown[];
}

// Discovered (non-official) Finnish source types that warrant the warning badge.
const FI_DISCOVERED_SOURCES = new Set([
  'ai_scan',
  'prescan',
  'osm_parking',
  'osm',        // generic OSM nature/amenity import
  'osm_nature',
]);

/**
 * Official Finnish vehicle surface (Digiroad rest area / rasteplass). These are legal
 * to overnight on and must NEVER show the warning badge.
 */
export function isFinnishOfficialSurface(spot: FinlandLegalWarningSpot | null | undefined): boolean {
  if (!spot) return false;
  const feats = (Array.isArray(spot.features) ? spot.features : []).map((f) => String(f).toUpperCase());
  if (feats.includes('RASTEPLASS')) return true;
  // Digiroad rest area: an explicit restType marks it as an official levähdysalue.
  if (spot.restType != null && String(spot.restType) !== '') return true;
  return false;
}

/**
 * Returns true when the amber Finnish legal-warning badge should be shown for a spot.
 * Note: callers still suppress it when a dedicated "Privat väg" badge is already shown
 * (roadOwnerCode === 3) to avoid duplicate amber badges.
 */
export function shouldShowFinlandLegalWarning(spot: FinlandLegalWarningSpot | null | undefined): boolean {
  if (!spot) return false;

  // Official rest areas / parking are legal — never warn, even if a stale flag exists.
  if (isFinnishOfficialSurface(spot)) return false;

  // Authoritative persisted server flag.
  if (spot.fiLegalWarning === true) return true;

  // Fallback: country + discovered source type (mirrors server isFinlandLegalVehicleSurface).
  const lat = spot.location?.latitude;
  const lon = spot.location?.longitude;
  if (typeof lat !== 'number' || typeof lon !== 'number') return false;
  if (detectCountry(lat, lon) !== 'FI') return false;

  const source = String(spot.source || '');
  if (source === 'digiroad_road' && spot.roadOwnerCode === 3) return true;
  if (FI_DISCOVERED_SOURCES.has(source)) return true;
  // AI-scan / prescan spots land as spotType 'algorithmic'.
  if (spot.spotType === 'algorithmic') return true;

  return false;
}
